import { Localized, useLocalization } from "@fluent/react"
import ArchiveIcon from '@mui/icons-material/Archive'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import PublishIcon from '@mui/icons-material/Publish'
import SaveIcon from '@mui/icons-material/Save'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, Paper, Stack, TextField,
    ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useBlocker, useNavigate, useParams } from "react-router-dom"
import { APIError } from "../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { CopyToClipboardButton } from "../../../app/components/CopyToClipboardButton"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { selectSettings } from "../../settings/settingsSlice"
import { selectIsTokenValid } from "../../user/userSlice"
import { DocumentLanguages } from "../../documents/documentTypes"
import { FormDefinition, FormFieldDef, FormFieldType, FormFieldValue } from "../../documents/sections/formSection/formSchema"
import { FormLoadState, selectFormDefinition, selectFormDefinitions } from "../../documents/sections/formSection/formSectionSlice"
import {
    createFormDefinition, deleteFormDefinition, FormDefinitionSave, loadFormDefinition, setFormDefinitionStatus,
    updateFormDefinition,
} from "../../documents/sections/formSection/formSectionApi"
import { useFormDefinitions } from "../../documents/sections/formSection/formSectionLoaders"
import { FormSectionView } from "../../documents/sections/formSection/FormSectionView"
import { fieldCaption, FieldOutline } from "./FieldOutline"
import { FieldProperties, IssueText, LockAdornment } from "./FieldProperties"
import {
    changeFieldType, cloneDefinition, DefinitionIssue, emptyDefinition, fieldLocks, keyFromLabel, newField, placeholdersOf,
    takenKeys, uniqueKey, validateDefinition,
} from "./formDefinitionRules"
import { FORM_DEFINITION_EDIT_PATH, FORM_DEFINITIONS_PATH, FormDefinitionStatusChip } from "./FormDefinitionList"

/** The server's message code is an l10n id; a stale save gets its own, anything unknown the generic one. */
const errorMessageId = (error: unknown) => {
    const e = error as APIError
    if (e?.kind !== 'http') return 'error'
    if (e.status === 409) return 'error-form-definition-stale'
    return e.problem?.title?.startsWith('form-def-error') || e.problem?.title?.startsWith('error-form') ? e.problem.title : 'error'
}

export const FormDefinitionEditor = () => {
    const { id } = useParams()
    const dispatch = useAppDispatch()
    const stored = useAppSelector(state => (id ? selectFormDefinition(state, id) : undefined))
    // The library is needed for the code-taken check and for cloning.
    useFormDefinitions()
    // Always fetch the edited definition: the version sent back on save must be the current one.
    const [loadState, setLoadState] = useState<FormLoadState>(id ? 'loading' : 'loaded')

    useEffect(() => {
        if (!id) return
        setLoadState('loading')
        dispatch(loadFormDefinition(id)).unwrap()
            .then(() => setLoadState('loaded'))
            .catch(() => setLoadState('error'))
    }, [dispatch, id])

    if (loadState === 'loading') return <LoadingContainer />
    if (id && !stored) {
        return <Alert severity="warning">
            <Localized id="form-def-not-found"><span>_Nie znaleziono definicji formularza.</span></Localized>
        </Alert>
    }
    // Saving a new definition navigates to its edit route, which remounts with the stored copy.
    return <Editor key={id ?? 'new'} stored={stored} />
}

/**
 * Edits a local copy; nothing reaches the library until Save. Locks are computed from `stored`, so a
 * field added in this session stays fully editable until the definition is published.
 */
const Editor = ({ stored }: { stored: FormDefinition | undefined }) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { l10n } = useLocalization()
    const uiLocale = useAppSelector(selectSettings).locale
    const all = useAppSelector(selectFormDefinitions)

    const [baseline, setBaseline] = useState<FormDefinition>(() => stored ?? emptyDefinition())
    const [draft, setDraft] = useState<FormDefinition>(baseline)
    const [selectedId, setSelectedId] = useState<string | null>(baseline.fields[0]?.id ?? null)
    /** Fields added in this session whose key still follows the label. Typing a key ends that. */
    const [autoKeys, setAutoKeys] = useState<Set<string>>(new Set())
    const [preview, setPreview] = useState(false)
    const [previewLanguage, setPreviewLanguage] = useState<DocumentLanguages>(uiLocale === 'pl' ? 'PL' : 'EN')
    const [previewValues, setPreviewValues] = useState<Record<string, FormFieldValue>>({})
    const [publishOpen, setPublishOpen] = useState(false)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [busy, setBusy] = useState(false)

    const readOnly = draft.status === 'ARCHIVED'
    const codeLocked = baseline.status !== 'DRAFT'
    const dirty = JSON.stringify(draft) !== JSON.stringify(baseline)

    // The blocker reads a ref so that save-then-navigate (a new definition) is not blocked by the
    // render that still sees the unsaved state.
    const dirtyRef = useRef(dirty)
    dirtyRef.current = dirty
    // Once the token is invalid the edits cannot be saved anyway, so let the redirect to login through.
    const tokenValid = useAppSelector(selectIsTokenValid)
    const tokenValidRef = useRef(tokenValid)
    tokenValidRef.current = tokenValid
    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        dirtyRef.current && tokenValidRef.current && currentLocation.pathname !== nextLocation.pathname)

    useEffect(() => {
        if (!tokenValid && blocker.state === 'blocked') blocker.proceed?.()
    }, [tokenValid, blocker])

    useEffect(() => {
        if (!dirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [dirty])

    const issues = useMemo(() => validateDefinition(draft, stored, all), [draft, stored, all])
    const errors = issues.filter(i => i.severity === 'error')
    const warnings = issues.filter(i => i.severity === 'warning')

    const selected = draft.fields.find(f => f.id === selectedId)

    const updateField = (fieldId: string, patch: Partial<FormFieldDef>) => setDraft(d => ({
        ...d,
        fields: d.fields.map(f => {
            if (f.id !== fieldId) return f
            const next = { ...f, ...patch }
            if (patch.label && autoKeys.has(fieldId)) {
                next.key = uniqueKey(keyFromLabel(patch.label.EN || patch.label.PL), takenKeys(d, stored, fieldId))
            }
            return next
        }),
    }))

    const changeKey = (fieldId: string, key: string) => {
        setAutoKeys(keys => { const next = new Set(keys); next.delete(fieldId); return next })
        updateField(fieldId, { key })
    }

    const changeType = (fieldId: string, type: FormFieldType) =>
        setDraft(d => ({ ...d, fields: d.fields.map(f => f.id === fieldId ? changeFieldType(f, type) : f) }))

    const addField = (type: FormFieldType) => {
        const field = newField(type, takenKeys(draft, stored))
        const at = selected ? draft.fields.indexOf(selected) + 1 : draft.fields.length
        setDraft(d => ({ ...d, fields: [...d.fields.slice(0, at), field, ...d.fields.slice(at)] }))
        setAutoKeys(keys => new Set(keys).add(field.id))
        setSelectedId(field.id)
        setPreview(false)
    }

    const removeField = (fieldId: string) => {
        const index = draft.fields.findIndex(f => f.id === fieldId)
        const rest = draft.fields.filter(f => f.id !== fieldId)
        setDraft(d => ({ ...d, fields: d.fields.filter(f => f.id !== fieldId) }))
        setSelectedId(rest[Math.min(index, rest.length - 1)]?.id ?? null)
    }

    /** Runs a server call with the buttons disabled; a failure shows the server's reason. */
    const run = async (action: () => Promise<void>) => {
        setBusy(true)
        try {
            await action()
        } catch (error) {
            dispatch(showError(errorMessageId(error)))
        } finally {
            setBusy(false)
        }
    }

    const toSave = (definition: FormDefinition): FormDefinitionSave =>
        ({ code: definition.code, name: definition.name, fields: definition.fields })

    // The server decides the version and the published keys; the editor takes its answer as the new baseline.
    const save = (publish: boolean) => run(async () => {
        const saved = stored
            ? await dispatch(updateFormDefinition({ id: stored.id, definition: toSave(draft), publish, expectedVersion: stored.version })).unwrap()
            : await dispatch(createFormDefinition({ definition: toSave(draft), publish })).unwrap()
        setBaseline(saved)
        setDraft(saved)
        setAutoKeys(new Set())
        setPublishOpen(false)
        dirtyRef.current = false
        dispatch(showSuccess('saved'))
        if (!stored) navigate(FORM_DEFINITION_EDIT_PATH(saved.id), { replace: true })
    })

    const setStatus = (status: 'PUBLISHED' | 'ARCHIVED') => run(async () => {
        const saved = await dispatch(setFormDefinitionStatus({ id: draft.id, status })).unwrap()
        setBaseline(b => ({ ...b, status: saved.status }))
        setDraft(d => ({ ...d, status: saved.status }))
    })

    const clone = () => run(async () => {
        const copy = cloneDefinition(draft, all, l10n.getString('form-def-copy-suffix', null, '(kopia)'))
        const saved = await dispatch(createFormDefinition({ definition: toSave(copy), publish: false })).unwrap()
        navigate(FORM_DEFINITION_EDIT_PATH(saved.id))
    })

    const remove = () => run(async () => {
        await dispatch(deleteFormDefinition(draft.id)).unwrap()
        dirtyRef.current = false
        navigate(FORM_DEFINITIONS_PATH)
    })

    const commitPreview = useCallback(
        (key: string, value: FormFieldValue) => setPreviewValues(values => ({ ...values, [key]: value })),
        [],
    )

    const allPlaceholders = draft.fields
        .flatMap(f => placeholdersOf(draft.code, f).map(p => p.tag))
        .join('\n')

    const issueList = (list: DefinitionIssue[]) => <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {list.map((issue, i) => {
            const field = draft.fields.find(f => f.id === issue.fieldId)
            return <li key={i}>
                {field && <><Link component="button" variant="body2" onClick={() => { setSelectedId(field.id); setPreview(false) }}>
                    {fieldCaption(field, uiLocale) || field.key}
                </Link>: </>}
                <IssueText issue={issue} />
            </li>
        })}
    </Box>

    return <Stack spacing={2}>
        <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" required label={l10n.getString('form-def-name', null, 'Nazwa')}
                        value={draft.name}
                        onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                        InputProps={{ readOnly }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" required label={l10n.getString('form-def-code', null, 'Kod')}
                        value={draft.code}
                        onChange={e => setDraft(d => ({ ...d, code: e.target.value.toUpperCase() }))}
                        InputProps={{
                            readOnly: readOnly || codeLocked,
                            endAdornment: codeLocked ? <LockAdornment /> : undefined,
                            sx: { fontFamily: 'monospace' },
                        }}
                        error={errors.some(i => i.target === 'code')}
                        helperText={(() => {
                            const issue = errors.find(i => i.target === 'code')
                            return issue ? <IssueText issue={issue} /> : undefined
                        })()}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 56 }}>
                    <FormDefinitionStatusChip status={draft.status} />
                    {draft.version > 0 && <Typography variant="body2" color="text.secondary">v{draft.version}</Typography>}
                    {dirty && <Typography variant="body2" color="warning.dark">
                        <Localized id="form-def-unsaved"><span>_Niezapisane zmiany</span></Localized>
                    </Typography>}
                </Grid>
            </Grid>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1 }}>
                {!readOnly && <Button variant="contained" startIcon={<SaveIcon />} disabled={busy || !dirty || errors.length > 0} onClick={() => save(false)}>
                    <Localized id="save"><span>_Zapisz</span></Localized>
                </Button>}
                {draft.status === 'DRAFT' && <Button variant="outlined" color="success" startIcon={<PublishIcon />}
                    disabled={busy || errors.length > 0} onClick={() => setPublishOpen(true)}>
                    <Localized id="form-def-publish"><span>_Opublikuj</span></Localized>
                </Button>}
                {draft.status === 'PUBLISHED' && <Button variant="outlined" startIcon={<ArchiveIcon />} disabled={busy || dirty}
                    onClick={() => setStatus('ARCHIVED')}>
                    <Localized id="form-def-archive"><span>_Archiwizuj</span></Localized>
                </Button>}
                {draft.status === 'ARCHIVED' && <Button variant="outlined" startIcon={<UnarchiveIcon />} disabled={busy}
                    onClick={() => setStatus('PUBLISHED')}>
                    <Localized id="form-def-restore"><span>_Przywróć</span></Localized>
                </Button>}
                {stored && <Button variant="outlined" startIcon={<ContentCopyIcon />} disabled={busy || dirty} onClick={clone}>
                    <Localized id="form-def-clone"><span>_Klonuj</span></Localized>
                </Button>}
                {stored && draft.status === 'DRAFT' && <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
                    onClick={() => setDeleteOpen(true)}>
                    <Localized id="delete"><span>_Usuń</span></Localized>
                </Button>}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        <Localized id="form-def-all-placeholders"><span>_Wszystkie znaczniki</span></Localized>
                    </Typography>
                    <CopyToClipboardButton value={allPlaceholders} disabled={draft.code === '' || allPlaceholders === ''} />
                    <ToggleButton value="preview" size="small" selected={preview} onChange={() => setPreview(p => !p)}>
                        <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Localized id="form-def-preview"><span>_Podgląd</span></Localized>
                    </ToggleButton>
                </Box>
            </Box>
        </Paper>

        {readOnly && <Alert severity="info">
            <Localized id="form-def-archived-hint"><span>_Formularz jest zarchiwizowany. Przywróć go, aby edytować.</span></Localized>
        </Alert>}
        {errors.length > 0 && <Alert severity="error">
            <Localized id="form-def-errors" vars={{ count: errors.length }}><span>_Popraw błędy przed zapisem:</span></Localized>
            {issueList(errors)}
        </Alert>}

        {preview
            ? <Paper sx={{ p: 2 }}>
                <ToggleButtonGroup size="small" exclusive value={previewLanguage} sx={{ mb: 2 }}
                    onChange={(_, language) => language && setPreviewLanguage(language)}>
                    <ToggleButton value="PL">PL</ToggleButton>
                    <ToggleButton value="EN">EN</ToggleButton>
                    <ToggleButton value="MULTI">PL / EN</ToggleButton>
                </ToggleButtonGroup>
                <FormSectionView fields={draft.fields} values={previewValues} language={previewLanguage}
                    readOnly={false} onCommit={commitPreview} />
            </Paper>
            : <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                        <FieldOutline
                            fields={draft.fields}
                            selectedId={selectedId}
                            issues={issues}
                            readOnly={readOnly}
                            onSelect={setSelectedId}
                            onReorder={fields => setDraft(d => ({ ...d, fields }))}
                            onAdd={addField}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2 }}>
                        {selected
                            ? <FieldProperties
                                key={selected.id}
                                field={selected}
                                code={draft.code}
                                locks={fieldLocks(stored, selected)}
                                issues={issues.filter(i => i.fieldId === selected.id)}
                                readOnly={readOnly}
                                onChange={patch => updateField(selected.id, patch)}
                                onTypeChange={type => changeType(selected.id, type)}
                                onKeyChange={key => changeKey(selected.id, key)}
                                onRemove={() => removeField(selected.id)}
                            />
                            : <Typography color="text.secondary">
                                <Localized id="form-def-select-field"><span>_Wybierz pole z listy albo dodaj nowe.</span></Localized>
                            </Typography>}
                    </Paper>
                </Grid>
            </Grid>}

        <Dialog open={publishOpen} onClose={() => setPublishOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle><Localized id="form-def-publish-title"><span>_Opublikować formularz?</span></Localized></DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 2 }}>
                    <Localized id="form-def-publish-locks">
                        <span>_Po publikacji nie można zmienić kodu, kluczy pól, typów pól ani wartości opcji. Usunięte klucze pozostaną zarezerwowane.</span>
                    </Localized>
                </Typography>
                {warnings.length > 0 && <Alert severity="warning">
                    <Localized id="form-def-publish-warnings"><span>_Brakuje tłumaczeń. Można opublikować mimo to:</span></Localized>
                    {issueList(warnings)}
                </Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPublishOpen(false)}><Localized id="cancel"><span>_Anuluj</span></Localized></Button>
                <Button variant="contained" color="success" disabled={busy} onClick={() => save(true)}>
                    <Localized id="form-def-publish"><span>_Opublikuj</span></Localized>
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
            <DialogContent><Localized id="confirm-delete"><span>_Czy napewno chcesz usunąć?</span></Localized></DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteOpen(false)}><Localized id="cancel"><span>_Anuluj</span></Localized></Button>
                <Button color="error" variant="contained" disabled={busy} onClick={remove}><Localized id="delete"><span>_Usuń</span></Localized></Button>
            </DialogActions>
        </Dialog>

        <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
            <DialogTitle><Localized id="unsaved-data"><span>_Niezapisane dane</span></Localized></DialogTitle>
            <DialogContent>
                <Typography><Localized id="unsaved-data-will-be-lost"><span>_Niezapisane dane zostaną utracone. Kontynuować?</span></Localized></Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => blocker.reset?.()}><Localized id="cancel"><span>_Anuluj</span></Localized></Button>
                <Button variant="contained" color="warning" onClick={() => blocker.proceed?.()}>
                    <Localized id="leave-without-saving"><span>_Wyjdź bez zapisywania</span></Localized>
                </Button>
            </DialogActions>
        </Dialog>
    </Stack>
}
